import {
  ApiSearchParameters,
  ApiSearchParameters as SapiSearchParameters,
  FilterParameters,
  HotelId,
  HotelOfferEntity as SapiHotelOfferEntity,
  PriceDisplay
} from '@findhotel/sapi/dist/types/packages/core/src/types'
import {SplitBookingDetails} from 'components/Offer/types'
import {OldHotelOfferEntity} from 'modules/sapiSearch/offersMapping'
import {FilterUrlParams, SearchUrlParams, SortOrder} from 'modules/search/types'
import {includes, isNil, move, omit, values} from 'ramda'
import typeCast, {AskedType} from 'utils/typeCast'

import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {Anchor, SearchParameters} from '@vio/core/src/sapi/types'
import {isOfferEligibleForDealFreeze} from '@vio/shared/src/dealFreeze/utils/OfferEligibility'

import {
  BaseOfferAccessFlowSettings,
  OfferAccessFlow,
  OfferAccessFlowSettings
} from './constants'
import {
  Hotel,
  HotelOfferEntity,
  HotelSearchParameters,
  LocationSearchParameters,
  PlaceSearchParameters,
  QuerySearchParameters,
  SearchOffer
} from './slice'

export enum SearchType {
  Hotel = 'hotel',
  Location = 'location',
  Place = 'place',
  Area = 'area',
  Address = 'address',
  Query = 'query',
  IATA = 'iata',
  Unknown = 'unknown'
}

export const getSearchParameter =
  (searchParameters: SearchUrlParams) =>
  (name: string, isMultiValue?: boolean, type?: AskedType) => {
    const parameter = searchParameters[name]

    if (isNil(parameter)) return undefined

    if (isMultiValue) {
      if (Array.isArray(parameter)) {
        return type ? parameter.map(param => typeCast(param, type)) : parameter
      }

      return type ? [typeCast(parameter, type)] : [parameter]
    }

    return type ? typeCast(parameter, type) : parameter
  }

// SAPI_TODO: unit test
export const getAnchorPrice = (
  offerEntity: HotelOfferEntity
): number | undefined => offerEntity?.anchorPriceRateBreakdown?.nightlyRate

export const getAnchorPriceTotal = (
  offerEntity: HotelOfferEntity
): number | undefined => offerEntity?.anchorPriceRateBreakdown?.totalRate

/**
 * Combines given offers list with a split booking offer injected on the right position
 * If the split booking offer is not present, returns the original list of offers
 *
 * @param offers - list of offers to combine with split booking offers
 * @param splitBookingDetails - split booking details
 * @returns list of offers with split booking offer injected on the right position
 */
const combineOffersWithSplitBookings = (
  offers: SearchOffer[],
  splitBookingDetails: SplitBookingDetails
): SearchOffer[] => {
  const {
    splitBookingOffers,
    hasSplitBookingOffer,
    offerPositionToRemove,
    splitBookingPosition
  } = splitBookingDetails

  if (!hasSplitBookingOffer) return offers

  const splitBookingOffersList = splitBookingOffers.map(i => i.offer) ?? []

  // For split booking, apply filtering logic and add the first offer of the bundle
  const filteredOffers = offers.filter((_, i) => i !== offerPositionToRemove)
  filteredOffers.splice(splitBookingPosition, 0, splitBookingOffersList[0])
  return filteredOffers
}
/**
 * Gets the top offers with the split booking offer injected on the right position
 */
export const getTopOffersWithSplitBookings = (
  hotelOfferEntity: HotelOfferEntity,
  splitBookingDetails: SplitBookingDetails
): SearchOffer[] => {
  const topOffers = getTopOffers(hotelOfferEntity) ?? []

  return combineOffersWithSplitBookings(topOffers, splitBookingDetails)
}

/**
 * Gets all offers with the split booking offer injected on the right position
 */
export const getAllOffersWithSplitBookings = (
  hotelOfferEntity: HotelOfferEntity,
  splitBookingDetails: SplitBookingDetails
): SearchOffer[] => {
  const offers = hotelOfferEntity?.offers ?? []

  return combineOffersWithSplitBookings(offers, splitBookingDetails)
}

export const getTopOffers = (offerEntity?: HotelOfferEntity): SearchOffer[] => {
  const topOffersLimit = Number(
    process.env.NEXT_PUBLIC_VISIBLE_TOP_OFFERS_LIMIT
  )
  return offerEntity?.offers?.slice(0, topOffersLimit) ?? []
}

/**
 * Whether the hotel has a Vio deal_freeze offer within its top 3 offers
 * @returns boolean
 */
export const hasFreezableTopOffer = (offerEntity?: HotelOfferEntity): boolean =>
  getTopOffers(offerEntity).some(offer =>
    isOfferEligibleForDealFreeze(offer.tags)
  )

/**
 * Type guard for searchParameters, allows safe access of `hotelId` prop
 * @returns boolean
 */
const isHotelSearchParams = (
  searchParameters: unknown
): searchParameters is HotelSearchParameters =>
  (searchParameters as HotelSearchParameters).hotelId !== undefined

/**
 * Type guard for searchParameters, allows safe access of `placeId` prop
 * @returns boolean
 */
const isPlaceSearchParams = (
  searchParameters: unknown
): searchParameters is PlaceSearchParameters =>
  (searchParameters as PlaceSearchParameters).placeId !== undefined

/**
 * Type guard for searchParameters, allows safe access of `geolocation` prop
 * @returns boolean
 */
const isLocationSearchParams = (
  searchParameters: unknown
): searchParameters is LocationSearchParameters =>
  (searchParameters as LocationSearchParameters).geolocation !== undefined

/**
 * Type guard for searchParameters, allows safe access of `query` prop
 * @returns boolean
 */
const isQuerySearchParams = (
  searchParameters: unknown
): searchParameters is QuerySearchParameters =>
  (searchParameters as QuerySearchParameters).query !== undefined

/**
 * Type guard for searchParameters, allows safe access of `boundingBox` prop
 * @returns boolean
 */
const isAreaSearchParams = (searchParameters: SapiSearchParameters) =>
  searchParameters.boundingBox !== undefined

/**
 * Type guard for searchParameters, allows safe access of `address` prop
 * @returns boolean
 */
const isAddressSearchParams = (
  searchParameters: SapiSearchParameters
): boolean => searchParameters.address !== undefined

/**
 * Type guard for searchParameters, allows safe access of `iataCode` prop
 * @returns boolean
 */
const isIATASearchParams = (searchParameters: SapiSearchParameters): boolean =>
  searchParameters.iataCode !== undefined

/**
 * Get the search type from search parameters
 * @returns SearchType string
 */
export const getSearchTypeFromSearchParameters = (
  searchParameters: SearchParameters | undefined
): SearchType => {
  if (searchParameters) {
    if (isHotelSearchParams(searchParameters)) return SearchType.Hotel
    if (isPlaceSearchParams(searchParameters)) return SearchType.Place
    if (isLocationSearchParams(searchParameters)) return SearchType.Location
    if (isQuerySearchParams(searchParameters)) return SearchType.Query
    if (isAreaSearchParams(searchParameters)) return SearchType.Area
    if (isAddressSearchParams(searchParameters)) return SearchType.Address
    if (isIATASearchParams(searchParameters)) return SearchType.IATA
  }

  return SearchType.Unknown
}

/**
 * Get the dealScore bucket 1 to 5 from a score
 * @param score - score between 0 and 1
 */
export const getBucketFromDealScore = (score: number): number => {
  if (score < 0.2) return 1
  if (score < 0.4) return 2
  if (score < 0.6) return 3
  if (score < 0.8) return 4
  return 5
}

/**
 * Get the regular price from a regularPriceRange
 * At the moment this is simply the average of the lower and upper bounds
 * @param range - regularPriceRange tuple, may miss the upper bound for the top bucket
 */
export const getRegularPrice = (
  range: [number, number | undefined]
): number => {
  const [lowerBound, upperBound] = range

  // Highest buckets have no upper bound so we cannot give a good indication of regular price
  if (!upperBound) return undefined

  return (lowerBound + upperBound) / 2
}

export const getTotalPrice = (
  rateBreakdown: SearchOffer['rate'],
  numberOfRooms: number
) =>
  (rateBreakdown.base + rateBreakdown.hotelFees + rateBreakdown.taxes) *
  numberOfRooms

/**
 * Validates if SAPI search parameters have all required parameters
 *
 * @param searchParameters - SearchParameters type
 * @returns Boolean
 */
export const areSapiSearchParamsValid = (
  searchParameters: SearchParameters | undefined
): boolean => {
  if (!searchParameters) return false

  const searchType = getSearchTypeFromSearchParameters(searchParameters)

  return includes(searchType, [
    SearchType.Hotel,
    SearchType.Place,
    SearchType.Area,
    SearchType.Location,
    SearchType.Address,
    SearchType.IATA
  ])
}

/**
 * Returns all filters from the `searchParameters`, formatted according to the SAPI implementation.
 *
 * @param searchParameters - Search parameters formatted according to the daedalus implementation
 * @returns All filters, formatted according to the SAPI implementation
 */
export const getSapiFiltersFromDaedalusSearchParameters = (
  searchParameters: FilterUrlParams
): FilterParameters => {
  const getParameter = getSearchParameter(searchParameters as SearchUrlParams)

  return {
    starRating: getParameter('starRatings', true, 'number'),
    guestRating: getParameter('reviewScore', true, 'number'),
    propertyTypeId: getParameter('propertyTypes', true, 'number'),
    facilities: getParameter('facilities', true, 'number'),
    themeIds: getParameter('themes', true, 'number'),
    chainIds: getParameter('chainIds', true, 'number'),
    noHostels: getParameter('noHostels', false, 'boolean'),
    priceMin: getParameter('priceMin', false, 'number'),
    priceMax: getParameter('priceMax', false, 'number'),
    hotelName: getParameter('hotelName'),
    freeCancellation: getParameter('freeCancellation', false, 'boolean'),
    amenities: getParameter('amenities', true, 'string'),
    payLater: getParameter('payLater', false, 'boolean'),
    brandIds: getParameter('brandIds', true, 'string'),
    neighborhoodIds: getParameter('neighborhoodIds', true, 'string'),
    exclusive: getParameter('exclusive', false, 'boolean'),
    dealFreeze: getParameter('dealFreeze', false, 'boolean') // Added as a part of c747927f-deal-freeze-srp
  }
}

// getting metadata base on urlParams
export const getMetadata = (
  esd?: string,
  epv?: string,
  vclid?: string,
  metaPrice?: string
): Pick<ApiSearchParameters, 'metadata'>['metadata'] | undefined => {
  if (!epv && !esd && !vclid && !metaPrice) return undefined

  return {
    ...(esd && {esd}),
    ...(epv && {epv}),
    ...(vclid && {vclid}),
    ...(metaPrice && {metaPrice})
  }
}

/**
 * Keeps only the attribution part of `metadata`, for requests outside the meta landing itinerary
 */
export const omitMetaLandingToken = (
  metadata: SearchParameters['metadata'] | undefined
): Pick<ApiSearchParameters, 'metadata'>['metadata'] | undefined =>
  getMetadata(undefined, undefined, metadata?.vclid)

/**
 * Determines whether any of the passed in `hotelOfferEntities` have offers
 */
export const hotelOfferEntitiesHaveOffers = (
  hotelOfferEntities?: Record<
    HotelId,
    SapiHotelOfferEntity | OldHotelOfferEntity
  >
) =>
  values(hotelOfferEntities).some(
    offerEntity => offerEntity?.offers?.length > 0
  )

/**
 * Returns a search location object based on the `navPathInfo` property from the `sapiObject`.
 */
export const getSearchLocationFromSapiObject = (sapiObject?: Anchor) => {
  const {adminDivision, city, country} = sapiObject?.navPathInfo || {}

  return {
    country: country?.name,
    region: adminDivision?.name,
    city: city?.name
  }
}

/**
 * [Search] Moves the prioritized offer to the top of the offers.
 * the criteria to move said offer is:
 * 1. If it's a "Your Choice" offer, it's moved to the top.
 * 2. If no "Your Choice" offer is present, the cheapest offer is moved to the top.
 * this is to make sure that the preferred rate goes on top and doesnt get locked.
 */
export const movePriorityOfferToTop = (
  offers: SearchOffer[]
): SearchOffer[] => {
  let offerIndex = -1

  offers.forEach((offer, index) => {
    if (hasOfferTag(offer, OfferTagLabel.IsYourChoice)) {
      offerIndex = index
    }
  })

  // If "Your Choice" offer isn't found, look for the cheapest offer
  if (offerIndex === -1) {
    offers.forEach((offer, index) => {
      if (
        offerIndex === -1 ||
        offer.nightlyRate < offers[offerIndex].nightlyRate
      ) {
        offerIndex = index
      }
    })
  }

  if (offerIndex !== -1) {
    return move(offerIndex, 0, offers)
  }

  return offers
}

/**
 * Determines whether a hotel has offers available.
 *
 * @param hotelOfferEntity - An object containing information about the hotel offers.
 * @returns boolean - Returns true if the hotel has at least one offer available, otherwise false.
 */
export const calculateHotelHasOffers = (
  hotelOfferEntity: HotelOfferEntity
): boolean => Boolean(hotelOfferEntity?.offers?.[0])

/**
 * Maps the provided hotel and its offer entity to the Daedalus format.
 *
 * @param hotel - The hotel entity.
 * @param hotelOfferEntity - The offer entity associated with the hotel.
 * @returns The hotel in Daedalus format.
 */
export const mapHotelToDaedalusFormat = (
  hotel: Hotel | undefined,
  hotelOfferEntity: HotelOfferEntity
) => {
  if (!hotel) return undefined
  return {
    ...hotel,
    offers: hotelOfferEntity?.offers,
    topOfferData: hotelOfferEntity?.topOfferData
  }
}

export const getHotelIdsWithOffers = (
  hotelIds: string[],
  hotelOfferEntities: Record<string, OldHotelOfferEntity>
): string[] => {
  return hotelIds.filter(
    hotelId => hotelOfferEntities?.[hotelId]?.offers?.length > 0
  )
}

export const convertAccessFlowConfig = (
  accessFlowConfig: BaseOfferAccessFlowSettings
): OfferAccessFlowSettings => {
  const {isLocked, flowType} = accessFlowConfig
  return {
    flowType,
    isLocked,
    isSpecial: flowType !== OfferAccessFlow.RegularDeal,
    isSpecialUnlocked: !isLocked && flowType !== OfferAccessFlow.RegularDeal,
    isAuthRequired: flowType === OfferAccessFlow.PrivateLockedDeal,
    isAppRequired: flowType === OfferAccessFlow.AppLocked,
    isAuthLocked: isLocked && flowType === OfferAccessFlow.PrivateLockedDeal,
    isAppLocked: isLocked && flowType === OfferAccessFlow.AppLocked,
    isAuthRequiredUnlocked:
      !isLocked && flowType === OfferAccessFlow.PrivateLockedDeal,
    isAppRequiredUnlocked: !isLocked && flowType === OfferAccessFlow.AppLocked
  }
}

/**
 * Remove SAPI params we only add for orchestration of API calls
 */
export const removeOrchestrationParams = (searchParameters: SearchParameters) =>
  omit(['userIp', 'skipOffers'], searchParameters) as SearchParameters

export const applyHotelEntitiesPriceSorting = (
  hotelOfferEntities: Record<string, OldHotelOfferEntity>,
  sortOrder: SortOrder,
  priceDisplay: PriceDisplay = 'nightly',
  isOptimizeRooms: boolean = false
) => {
  const isAscending = sortOrder === 'ascending'
  const shouldSortByTotalRate = priceDisplay === 'total' || isOptimizeRooms
  // Hotels without offers always end up last
  const rateIfNoOffers = isAscending ? Infinity : 0

  const getSortRate = (offers: SearchOffer[]) => {
    const [topOffer] = offers
    return (
      (shouldSortByTotalRate
        ? topOffer?.totalRate
        : topOffer?.calculatedTotalRate) ?? rateIfNoOffers
    )
  }

  return Object.values(hotelOfferEntities)
    .sort((a, b) => {
      const rateDifference = getSortRate(a.offers) - getSortRate(b.offers)
      return isAscending ? rateDifference : -rateDifference
    })
    .map(({id}) => id)
}
